// Analysis view — the real product surface.
// Two personas share the same record: Investor (triage, thesis, peers, decision log)
// and Founder (coaching queue, how-investors-scored-you, gap-closing).

import React, { useState, useEffect } from "react";
import { MOCK } from "./data.jsx";
import { Ico, IASMeter, Radar, StatusPill } from "./primitives.jsx";
import { apiGet, apiPatch, apiPost, ApiError, isDemoMode, navigate } from "./api.jsx";

const useState_R = useState;

export function ResultsView({ role = "investor", setRole }) {
  const demo = isDemoMode();
  // ── ALL hooks first. Rules of Hooks forbid conditional hook calls,
  //    so every useState/useEffect/useMemo must run on every render in
  //    the same order — regardless of whether we early-return for an
  //    empty / loading / error state below.
  const [analysisId, setAnalysisId] = useState_R(null);
  const [analysis, setAnalysis] = useState_R(null);
  const [loading, setLoading] = useState_R(false);
  const [loadError, setLoadError] = useState_R(null);
  const [saving, setSaving] = useState_R(false);
  const [saveNotice, setSaveNotice] = useState_R(null);
  const [tab, setTab] = useState_R("thesis");
  const [thesisEdited, setThesisEdited] = useState_R(false);
  const [thesisText, setThesisText] = useState_R("");
  const [activeSlide, setActiveSlide] = useState_R(null);

  useEffect(() => {
    try { setAnalysisId(localStorage.getItem("vz-analysis-id")); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!analysisId || demo) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    apiGet(`/analyses/${analysisId}/`)
      .then(data => { if (!cancelled) { setAnalysis(data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setLoadError(err); setLoading(false); } });
    return () => { cancelled = true; };
  }, [analysisId, demo]);

  // Derived values (pure — not hooks, safe to compute every render)
  const mockCo = MOCK.companies[0];
  const company = analysis ? {
    id: analysis.id,
    name: analysis.name,
    tech: analysis.modality || null,
    stage: analysis.stage || null,
    inv: analysis.raise_amount || null,
    status: analysis.status || "review",
    type: analysis.type || null,
    ias: analysis.ias ?? null,
  } : mockCo;
  const ias = company.ias ?? 82;
  const realThesis = analysis?.thesis?.text ? analysis.thesis : null;
  const isFounder = role === "founder";

  // Re-sync thesis text when the analysis loads. Also seeds a demo draft
  // when we don't have a real record (only used in ?demo=1 preview).
  useEffect(() => {
    if (realThesis?.text) {
      setThesisText(realThesis.text);
      setThesisEdited(false);
    } else if (!analysis && demo) {
      setThesisText(MOCK.thesis.draft);
    } else if (!analysis && !analysisId) {
      setThesisText("");
    }
  }, [realThesis?.text, analysis, demo, analysisId]);

  // Default tab for role (must live AFTER tab useState above).
  useEffect(() => {
    setTab(isFounder ? "coaching" : "thesis");
  }, [isFounder]);

  // ── THEN the early-return branches. These are safe because every hook
  //    above has already been called.

  if (!analysisId && !demo) {
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div className="h-eyebrow" style={{marginBottom:10}}>Analysis view</div>
          <h1 className="page-title">No analysis <em>selected</em>.</h1>
          <p className="page-sub" style={{marginTop:12, maxWidth:640}}>
            Choose a record from your pipeline or dashboard to open it here.
          </p>
          <div className="row" style={{marginTop:20, gap:10}}>
            <button className="btn btn--primary" onClick={() => navigate("pipeline")}>Go to pipeline</button>
            <button className="btn btn--ghost" onClick={() => navigate("dashboard")}>Back to dashboard</button>
          </div>
        </div>
      </div>
    );
  }
  if (loading && !demo) {
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div className="h-eyebrow">Loading…</div>
          <h1 className="page-title" style={{opacity:.5}}>Opening record</h1>
        </div>
      </div>
    );
  }
  if (loadError && !analysis && !demo) {
    const status = loadError.status;
    const msg = status === 404 ? "That analysis no longer exists (or it's not scoped to your account)."
             : status === 403 ? "You don't have access to this analysis."
             : status === 401 ? "Please sign in to view this analysis."
             : (loadError.body?.error || loadError.message || "Couldn't load the analysis.");
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div className="h-eyebrow" style={{color:"var(--bad)"}}>Couldn't load</div>
          <h1 className="page-title">{msg}</h1>
          <div className="row" style={{marginTop:20, gap:10}}>
            <button className="btn btn--primary" onClick={() => navigate("pipeline")}>Back to pipeline</button>
            <button className="btn btn--ghost" onClick={() => { try { localStorage.removeItem("vz-analysis-id"); } catch {} navigate("dashboard"); }}>Clear selection</button>
          </div>
        </div>
      </div>
    );
  }

  const saveThesis = async () => {
    if (!analysisId) return;
    setSaving(true);
    setSaveNotice(null);
    try {
      await apiPatch(`/analyses/${analysisId}/thesis/`, { text: thesisText });
      setSaveNotice("Saved.");
      setThesisEdited(false);
    } catch (err) {
      setSaveNotice(err instanceof ApiError ? (err.body?.error || err.message) : err.message);
    } finally {
      setSaving(false);
    }
  };

  const moveStatus = async (newStatus) => {
    if (!analysisId) return;
    try {
      const updated = await apiPatch(`/analyses/${analysisId}/status/`, { status: newStatus });
      setAnalysis(updated);
    } catch (err) {
      console.error("Status move failed", err);
    }
  };

  const tabs = isFounder
    ? [["coaching","How to strengthen"], ["scoring","How investors scored you"], ["peers","Where you stand"], ["timeline","Activity"]]
    : [["thesis","Thesis"], ["scoring","Score"], ["citations","Evidence"], ["peers","Peers"], ["timeline","Activity"]];

  return (
    <div className="page page--wide route-enter">
      {/* Record header */}
      <div style={{
        display:"grid", gridTemplateColumns:"minmax(0,1fr) auto", gap:24, alignItems:"end",
        paddingBottom:24, borderBottom:"1px solid var(--line)", marginBottom:28
      }}>
        <div style={{minWidth:0}}>
          <div className="row" style={{gap:12, marginBottom:10, flexWrap:"wrap"}}>
            <div className="h-eyebrow" style={{whiteSpace:"nowrap"}}>Analysis record · {analysisId ? `#${analysisId}` : "HLX-20260414"}</div>
            <RoleToggle role={role} setRole={setRole}/>
          </div>
          <h1 className="page-title">{analysis ? company.name : <>Helix <em>Therapeutics</em></>}</h1>
          <div className="row" style={{marginTop:12, rowGap:8, columnGap:14, color:"var(--text-mute)", fontSize:14, flexWrap:"wrap"}}>
            <span style={{whiteSpace:"nowrap"}}>{company.tech}</span>
            <span className="dim">·</span>
            <span style={{whiteSpace:"nowrap"}}>{[company.stage, company.inv].filter(Boolean).join(" · ")}</span>
            {analysis?.location && <><span className="dim">·</span><span style={{whiteSpace:"nowrap"}}>{analysis.location}</span></>}
            <StatusPill status={company.status}/>
            <span className="as-of" style={{whiteSpace:"nowrap"}}>
              {analysis?.updated_at ? `Updated ${new Date(analysis.updated_at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"})}` : "Updated 14 min ago"}
            </span>
          </div>
        </div>
        <div className="row" style={{gap:10, flexShrink:0}}>
          {isFounder ? (
            <>
              <button
                className="btn btn--ghost"
                style={{whiteSpace:"nowrap"}}
                onClick={() => { if (analysisId) location.href = `/dbview/output/${analysisId}/report/`; }}
                disabled={!analysisId}
                title={analysisId ? "Open the full Django report for this analysis" : "Select an analysis first"}
              >Download report</button>
              <button
                className="btn btn--primary"
                style={{whiteSpace:"nowrap"}}
                onClick={() => navigate("analyzer")}
              >Request re-score</button>
            </>
          ) : (
            <>
              <button
                className="btn btn--ghost"
                style={{whiteSpace:"nowrap"}}
                onClick={() => {
                  const url = location.origin + "/?a=" + (analysisId || "");
                  try {
                    if (navigator.clipboard) { navigator.clipboard.writeText(url); alert("Link copied to clipboard."); }
                    else { window.prompt("Copy this link:", url); }
                  } catch { window.prompt("Copy this link:", url); }
                }}
                disabled={!analysisId}
              >Share</button>
              <button
                className="btn btn--ghost"
                style={{whiteSpace:"nowrap"}}
                onClick={() => { if (analysisId) location.href = `/dbview/output/${analysisId}/report/`; }}
                disabled={!analysisId}
                title={analysisId ? "Open the full Django report (memo view)" : "Select an analysis first"}
              >Export memo</button>
              {company.status === "review" && (
                <button className="btn btn--ghost" onClick={() => moveStatus("shortlist")} style={{whiteSpace:"nowrap"}}>→ Shortlist</button>
              )}
              {company.status !== "diligence" && (
                <button className="btn btn--primary" onClick={() => moveStatus("diligence")} style={{whiteSpace:"nowrap"}}>Advance to diligence</button>
              )}
              {company.status === "diligence" && (
                <button className="btn btn--ghost" onClick={() => moveStatus("pass")} style={{whiteSpace:"nowrap"}}>Pass</button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Thesis banner — investor only. The single most important surface. */}
      {!isFounder && (
        <div className="thesis-banner" style={{marginBottom:28}}>
          <div className="thesis-verdict">
            <div className="h-eyebrow">Verdict {realThesis ? `· ${realThesis.edited ? "edited" : "AI-drafted"}` : "· AI-drafted"}</div>
            <div className="thesis-verdict-value">{realThesis?.verdict || MOCK.thesis.verdict}</div>
            <div className="dim" style={{fontSize:12, marginTop:6}}>
              {realThesis?.auto_drafted ? "Auto-drafted from extracted evidence. Edit to make it yours." : "Edit to keep it aligned with your current read."}
            </div>
          </div>
          <div style={{flex:1}}>
            <div className="h-eyebrow" style={{marginBottom:8}}>One-paragraph thesis</div>
            <textarea
              className="thesis-textarea"
              value={thesisText}
              onChange={(e)=>{setThesisText(e.target.value); setThesisEdited(true);}}
              rows={5}
            />
            <div className="row" style={{marginTop:10, gap:10}}>
              <div className="as-of">
                {saveNotice
                  ? saveNotice
                  : thesisEdited
                    ? "Edited · not yet saved"
                    : (realThesis?.author || MOCK.thesis.author)}
              </div>
              <div className="spacer"/>
              <button
                className="btn btn--quiet"
                onClick={()=>{ setThesisText(realThesis?.text || MOCK.thesis.draft); setThesisEdited(false); setSaveNotice(null); }}
              >
                Reset
              </button>
              <button
                className="btn btn--primary"
                onClick={saveThesis}
                disabled={saving || !analysisId || !thesisEdited}
                title={analysisId ? "" : "Select an analysis before saving the thesis"}
              >
                {saving ? "Saving…" : "Save thesis"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Founder-side hero: what investors will ask */}
      {isFounder && (
        <FounderHero coaching={MOCK.coachingQueue} setActiveSlide={setActiveSlide}/>
      )}

      {/* Tabs */}
      <div className="tabs-row">
        {tabs.map(([k,lbl])=>(
          <div key={k} onClick={()=>setTab(k)}
               className={"tab-item" + (tab===k ? " on" : "")}>
            {lbl}
          </div>
        ))}
      </div>

      {/* Split layout: main tab content + persistent evidence rail */}
      <div className="grid results-split" style={{gridTemplateColumns:"1.55fr 1fr", gap:28, alignItems:"start"}}>
        <div className="stack">
          {tab==="thesis" && <TabInvestorOverview analysisId={analysisId} analysis={analysis}/>}
          {tab==="coaching" && <TabFounderCoaching setActiveSlide={setActiveSlide}/>}
          {tab==="scoring" && (isFounder ? <TabFounderScoring/> : <TabInvestorScoring/>)}
          {tab==="citations" && <TabCitations setActiveSlide={setActiveSlide} analysis={analysis}/>}
          {tab==="peers" && <TabPeers analysis={analysis}/>}
          {tab==="timeline" && <TabTimeline role={role} analysis={analysis}/>}
        </div>

        <div className="stack" style={{position:"sticky", top:80}}>
          <EvidenceRail activeSlide={activeSlide} setActiveSlide={setActiveSlide} analysis={analysis} analysisId={analysisId}/>
          <ReportsCard analysis={analysis}/>
          {!isFounder && <DecisionLog analysis={analysis}/>}
          {isFounder && <FounderNextSteps/>}
          <DocumentsCard analysis={analysis} analysisId={analysisId}/>
        </div>
      </div>
    </div>
  );
}

export function RoleToggle({ role, setRole }) {
  return (
    <div className="seg seg--mini">
      <button className={role==="investor"?"on":""} onClick={()=>setRole && setRole("investor")}>Investor view</button>
      <button className={role==="founder"?"on":""} onClick={()=>setRole && setRole("founder")}>Founder view</button>
    </div>
  );
}

// ===== Investor tabs =====

export function TabInvestorOverview({ analysisId, analysis }) {
  // Prefer the real analysis data. Only fall back to MOCK when we're rendering
  // without a real record (demo preview).
  const hasReal = !!analysis;
  const fields = hasReal ? (analysis.analysis_fields || []) : MOCK.analysisFields;
  const missing = hasReal ? (analysis.missing || []) : MOCK.missing;
  const summary = hasReal
    ? (analysis.summary || "")
    : "Helix Therapeutics is developing an mRNA lipid-nanoparticle platform for HER2+ solid tumors, with lead preclinical asset HLX-101 showing tumor regression in xenograft models. A $18M Series A is sought to fund IND-enabling studies through Q4 2027.";

  // Build a label → citation lookup from the API's citation list (or MOCK).
  const citationList = hasReal ? (analysis.citations || []) : Object.entries(MOCK.citations).map(([field, c]) => ({ field, slide: c.slide, quote: c.quote }));
  const citationByLabel = {};
  for (const c of citationList) {
    if (c.field) citationByLabel[c.field.toLowerCase()] = c;
  }

  return (
    <>
      <div className="card">
        <div className="h-eyebrow">Extracted summary</div>
        {summary ? (
          <p style={{fontSize:"var(--text-md)", lineHeight:1.65, marginTop:12}}>{summary}</p>
        ) : (
          <p className="dim" style={{fontSize:"var(--text-md)", lineHeight:1.65, marginTop:12, fontStyle:"italic"}}>
            No one-paragraph summary extracted for this deck yet. The structured fields below are still
            available.
          </p>
        )}
        {fields.length > 0 ? (
          <div className="grid grid--3" style={{gap:14, marginTop:20}}>
            {fields.map(f => {
              const cite = citationByLabel[(f.label || "").toLowerCase()];
              return (
                <div key={f.label}>
                  <div className="h-eyebrow">{f.label}</div>
                  <div style={{fontSize:14, marginTop:4}}>
                    {f.value}
                    {cite && cite.slide != null && <sup className="cite">§{cite.slide}</sup>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : hasReal ? (
          <p className="dim" style={{fontSize:13, marginTop:20, fontStyle:"italic"}}>
            No structured fields were extracted for this analysis.
          </p>
        ) : null}
      </div>

      {missing.length > 0 && (
        <div className="card">
          <div className="h-eyebrow">Missing & flagged for diligence</div>
          <div style={{marginTop:12}}>
            {missing.map((m, i) => (
              <div key={i} style={{display:"flex", gap:14, padding:"12px 0", borderBottom: i < missing.length - 1 ? "1px solid var(--line)" : 0, alignItems:"center"}}>
                <div style={{width:20, height:20, borderRadius:"50%", border:"1px solid var(--warn)", color:"var(--warn)", fontSize:11, display:"grid", placeItems:"center", fontFamily:"var(--ff-mono)"}}>!</div>
                <div style={{flex:1, fontSize:14}}>{m}</div>
                <button
                  className="btn btn--quiet"
                  onClick={() => { if (analysisId) location.href = `/dbview/output/${analysisId}/missing_parts/`; }}
                  disabled={!analysisId}
                  title={analysisId ? "Open the missing-parts report" : "Select an analysis first"}
                >View →</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export function TabInvestorScoring() {
  const ias = 82;
  return (
    <div className="card">
      <div className="row">
        <div className="h-eyebrow">Investment attractiveness score</div>
        <div className="spacer"/>
        <button className="btn btn--quiet" onClick={()=>window.showIASLegend?.()}>How is IAS calculated? →</button>
      </div>
      <div style={{display:"grid", gridTemplateColumns:"auto 1fr", gap:28, marginTop:16, alignItems:"center"}}>
        <div style={{textAlign:"center"}}>
          <IASMeter value={ias} size={200}/>
          <div className="dim" style={{fontSize:13, marginTop:6}}>Top 12% · Therapeutic · Series A</div>
          <div className="as-of" style={{marginTop:4}}>+3 vs. peer median</div>
        </div>
        <div style={{minWidth:0, overflow:"hidden"}}>
          <Radar data={MOCK.iasBreakdown} size={280}/>
        </div>
      </div>
      <hr className="hr" style={{margin:"20px 0"}}/>
      <div style={{display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:10}}>
        {MOCK.iasBreakdown.map(b=>(
          <div key={b.key} style={{borderLeft:"1px solid var(--line)", paddingLeft:10}}>
            <div className="h-eyebrow" style={{fontSize:10}}>{b.key}</div>
            <div className="row" style={{marginTop:4, gap:6, alignItems:"baseline"}}>
              <span className="num" style={{fontSize:20, fontWeight:500}}>{b.score}</span>
              <span className="dim" style={{fontSize:11}}>w{(b.weight*100).toFixed(0)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TabCitations({ setActiveSlide, analysis }) {
  // Prefer real citations from the API; fall back to MOCK only when we don't
  // have a real record (demo preview).
  const hasReal = !!analysis;
  const entries = hasReal
    ? (analysis.citations || []).map(c => [c.field, { slide: c.slide, quote: c.quote }])
    : Object.entries(MOCK.citations);

  return (
    <div className="card card--flush">
      <div className="card-header">
        <div>
          <div className="eyebrow">Evidence · every claim ties to a slide</div>
          <div className="title">Extracted fields &amp; their sources</div>
        </div>
        {entries.length > 0 && <div className="as-of">{entries.length} {entries.length === 1 ? "citation" : "citations"}</div>}
      </div>
      {entries.length === 0 ? (
        <div className="dim" style={{padding:"24px 20px", fontSize:13, fontStyle:"italic", textAlign:"center"}}>
          No slide-level citations extracted for this analysis yet.
        </div>
      ) : (
        <table className="t">
          <thead>
            <tr>
              <th style={{width:140}}>Field</th>
              <th>Extracted value</th>
              <th style={{width:90, textAlign:"right"}}>Slide</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([field, {slide, quote}], i) => (
              <tr key={field + i} style={{cursor:"pointer"}} onClick={() => slide != null && setActiveSlide(slide)}>
                <td><div className="h-eyebrow" style={{fontSize:10}}>{field}</div></td>
                <td style={{fontSize:13, lineHeight:1.5}}>{quote ? `"${quote}"` : "—"}</td>
                <td style={{textAlign:"right"}}>
                  {slide != null ? <span className="cite-pill">§{slide}</span> : <span className="dim">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function TabPeers({ analysis }) {
  const hasReal = !!analysis;
  const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString("en-US", { month: "short", year: "numeric" }); }
    catch { return "—"; }
  };

  const me = hasReal
    ? {
        name: analysis.name,
        ias: analysis.ias ?? null,
        raise: analysis.raise_amount || "—",
        stage: analysis.stage || "—",
        date: fmtDate(analysis.updated_at),
        mine: true,
      }
    : { name: "Helix Therapeutics", ias: 82, raise: "$18M", stage: "Series A", date: "Apr 2026", mine: true };

  const peers = hasReal
    ? (analysis.peers || []).map(p => ({
        name: p.name,
        ias: p.ias ?? null,
        raise: p.raise || "—",
        stage: p.stage || "—",
        date: fmtDate(p.date),
        diff: null,
      }))
    : MOCK.peers;

  const rows = [me, ...peers];
  const max = 100;
  return (
    <>
      <div className="card">
        <div className="h-eyebrow">Peer comparison · {peers.length} closest {peers.length === 1 ? "analysis" : "analyses"}</div>
        <div className="dim" style={{fontSize:13, marginTop:6, lineHeight:1.5}}>
          {hasReal
            ? `Same modality (${analysis.modality || "—"}), last 12 months.`
            : "Same modality (mRNA-LNP or adjacent), same stage (Series A), last 12 months."}
        </div>
        {peers.length === 0 ? (
          <p className="dim" style={{fontSize:13, marginTop:16, fontStyle:"italic"}}>
            No comparables yet in this modality.
          </p>
        ) : (
          <div style={{marginTop:20}}>
            {rows.map((r, i) => (
              <div key={r.name + i} style={{
                display:"grid", gridTemplateColumns:"180px 1fr 80px 80px",
                gap:14, alignItems:"center",
                padding:"14px 0",
                borderTop: i ? "1px solid var(--line)" : 0
              }}>
                <div>
                  <div style={{fontSize:14, fontWeight: r.mine ? 600 : 400, color: r.mine ? "var(--accent-hi)" : "var(--text)"}}>
                    {r.name} {r.mine && <span className="as-of" style={{marginLeft:6}}>THIS DEAL</span>}
                  </div>
                  <div className="dim" style={{fontSize:12, marginTop:2}}>{r.date} · {r.diff || (r.mine ? "you" : "peer")}</div>
                </div>
                <div>
                  <div style={{height:8, background:"var(--line)", borderRadius:4, overflow:"hidden", position:"relative"}}>
                    <div style={{
                      width: r.ias != null ? `${(r.ias / max) * 100}%` : 0,
                      height:"100%",
                      background: r.mine ? "var(--accent-hi)" : "var(--text-dim)"
                    }}/>
                  </div>
                </div>
                <div className="num" style={{fontSize:16, textAlign:"right", fontWeight: r.mine ? 600 : 400}}>{r.ias ?? "—"}</div>
                <div className="dim num" style={{fontSize:13, textAlign:"right"}}>{r.raise}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export function PeerStat({ label, value, direction }) {
  const arrow = direction==="up" ? "▲" : direction==="down" ? "▼" : "—";
  const color = direction==="up" ? "var(--good)" : direction==="down" ? "var(--bad)" : "var(--text-mute)";
  return (
    <div style={{borderLeft:"1px solid var(--line)", paddingLeft:14}}>
      <div className="h-eyebrow">{label}</div>
      <div className="row" style={{gap:8, marginTop:6, alignItems:"baseline"}}>
        <span className="num" style={{fontSize:22, fontWeight:500}}>{value}</span>
        <span style={{color, fontSize:12}}>{arrow}</span>
      </div>
    </div>
  );
}

export function TabTimeline({ role, analysis }) {
  const hasReal = !!analysis;
  const log = hasReal
    ? (analysis.decision_log || []).map(e => ({
        t: e.at ? new Date(e.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—",
        who: e.actor || "system",
        action: e.note || _describeAction(e.action, e.meta),
        kind: e.action,
        from: e.meta && e.meta.from,
        to: e.meta && e.meta.to,
      }))
    : MOCK.decisionLog;

  return (
    <div className="card card--flush">
      <div className="card-header">
        <div>
          <div className="eyebrow">Activity · decision trail</div>
          <div className="title">{role==="founder" ? "Investor actions on your deck" : "Team decisions & system events"}</div>
        </div>
      </div>
      {log.length === 0 ? (
        <div className="dim" style={{padding:"24px 20px", fontSize:13, fontStyle:"italic", textAlign:"center"}}>
          No activity recorded yet. Moving this deck through the pipeline, editing the thesis, or leaving notes shows up here.
        </div>
      ) : (
        <div>
        {log.map((e,i)=>{
          const icon = e.kind==="flag" ? "!" : e.kind==="move" ? "→" : e.kind==="score" ? "●" : "◯";
          const color = e.kind==="flag" ? "var(--warn)" : e.kind==="move" ? "var(--accent-hi)" : "var(--text-mute)";
          return (
            <div key={i} style={{
              display:"grid", gridTemplateColumns:"100px 32px 1fr",
              gap:16, padding:"16px 20px",
              borderBottom: i<log.length-1 ? "1px solid var(--line)" : 0,
              alignItems:"center"
            }}>
              <div className="as-of">{e.t}</div>
              <div style={{
                width:24, height:24, borderRadius:"50%",
                border:`1px solid ${color}`, color,
                display:"grid", placeItems:"center",
                fontFamily:"var(--ff-mono)", fontSize:12
              }}>{icon}</div>
              <div>
                <div style={{fontSize:14, lineHeight:1.5}}>{e.action}</div>
                <div className="dim" style={{fontSize:12, marginTop:2}}>
                  {e.who}
                  {e.from && <> · {e.from} → {e.to}</>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function _describeAction(action, meta) {
  switch (action) {
    case "move":
      return meta && meta.from && meta.to
        ? `Moved from ${meta.from} to ${meta.to}`
        : "Pipeline stage changed";
    case "score": return "Re-scored";
    case "flag": return "Flagged for review";
    case "note": return "Note added";
    case "thesis_adopt": return "Thesis adopted";
    case "assign": return "Reassigned";
    default: return action ? `${action[0].toUpperCase()}${action.slice(1)}` : "Activity";
  }
}

// ===== Founder tabs =====

function FounderHero({ coaching, setActiveSlide }) {
  const criticals = coaching.filter(c=>c.priority==="critical").length;
  const highs     = coaching.filter(c=>c.priority==="high").length;
  return (
    <div className="founder-hero" style={{marginBottom:28}}>
      <div>
        <div className="h-eyebrow">What investors will ask</div>
        <h2 className="h-display" style={{fontSize:"var(--text-2xl)", marginTop:8, lineHeight:1.2}}>
          {coaching.length} questions to answer before your next meeting. <em>{criticals} are critical.</em>
        </h2>
        <div className="dim" style={{fontSize:14, marginTop:10, lineHeight:1.5, maxWidth:640}}>
          Venzyme read your deck like a Series A life-sciences lead would. These are the specific questions
          they'll raise, ranked by how much they matter.
        </div>
      </div>
      <div className="founder-hero-stats">
        <div><div className="num" style={{fontSize:36, color:"var(--bad)"}}>{criticals}</div><div className="as-of">CRITICAL</div></div>
        <div><div className="num" style={{fontSize:36, color:"var(--warn)"}}>{highs}</div><div className="as-of">HIGH</div></div>
        <div><div className="num" style={{fontSize:36, color:"var(--text-mute)"}}>{coaching.length - criticals - highs}</div><div className="as-of">MEDIUM</div></div>
      </div>
    </div>
  );
}

function TabFounderCoaching({ setActiveSlide }) {
  const pBadge = {
    critical: { label: "CRITICAL", color: "var(--bad)" },
    high:     { label: "HIGH",     color: "var(--warn)" },
    medium:   { label: "MEDIUM",   color: "var(--text-mute)" },
  };
  // Local state — these annotations live in the browser for now.
  // Persisting them to the backend will come with the founder portal in a follow-up.
  const [status, setStatus] = useState_R({});
  const [drafts, setDrafts] = useState_R({});

  return (
    <div className="stack">
      {MOCK.coachingQueue.map((c,i)=>{
        const p = pBadge[c.priority];
        const s = status[i];
        const isAddressed = s === "addressed";
        const isDismissed = s === "dismissed";
        const draftingKey = "draft-" + i;
        return (
          <div key={i} className="card" style={{
            opacity: isDismissed ? 0.5 : 1,
            borderColor: isAddressed ? "var(--good)" : undefined,
          }}>
            <div className="row" style={{gap:10, marginBottom:10}}>
              <span className="h-eyebrow" style={{color: isAddressed ? "var(--good)" : p.color}}>
                ◉ {isAddressed ? "ADDRESSED" : isDismissed ? "NOT RELEVANT" : p.label}
              </span>
              <span className="dim" style={{fontSize:12}}>· Question {i+1} of {MOCK.coachingQueue.length}</span>
              <div className="spacer"/>
              <button className="btn btn--quiet" onClick={()=>setActiveSlide(c.slide)}>
                See slide §{c.slide} →
              </button>
            </div>
            <div style={{
              fontFamily:"var(--ff-h)",
              fontSize:"var(--text-xl)",
              lineHeight:1.25,
              letterSpacing:"-0.015em",
              textDecoration: isDismissed ? "line-through" : "none",
            }}>
              "{c.q}"
            </div>
            <div style={{
              marginTop:14,
              padding:"14px 16px",
              background:"var(--surface-2)",
              borderLeft:"2px solid var(--accent)",
              borderRadius:"2px",
              fontSize:14, lineHeight:1.6
            }}>
              <div className="h-eyebrow" style={{marginBottom:6}}>Why they'll ask</div>
              {c.why}
            </div>
            {drafts[draftingKey] !== undefined && (
              <textarea
                value={drafts[draftingKey]}
                onChange={(e) => setDrafts({ ...drafts, [draftingKey]: e.target.value })}
                placeholder="Draft your answer here. Nothing is saved on the server yet — copy out when you're happy."
                rows={4}
                style={{
                  width: "100%", boxSizing: "border-box",
                  marginTop: 14,
                  padding: "12px",
                  background: "var(--surface)",
                  border: "1px dashed var(--line-strong)",
                  borderRadius: 4,
                  fontFamily: "inherit",
                  fontSize: 14,
                  color: "var(--text)",
                  outline: "none",
                  resize: "vertical",
                }}
              />
            )}
            <div className="row" style={{marginTop:14, gap:8}}>
              <button
                className="btn btn--ghost"
                onClick={() => setDrafts({ ...drafts, [draftingKey]: drafts[draftingKey] ?? "" })}
                disabled={drafts[draftingKey] !== undefined}
              >
                {drafts[draftingKey] !== undefined ? "Drafting…" : "Draft an answer"}
              </button>
              <button
                className="btn btn--quiet"
                onClick={() => setStatus({ ...status, [i]: isAddressed ? undefined : "addressed" })}
              >
                {isAddressed ? "✓ Addressed" : "Mark as addressed"}
              </button>
              <div className="spacer"/>
              <button
                className="btn btn--quiet"
                onClick={() => setStatus({ ...status, [i]: isDismissed ? undefined : "dismissed" })}
              >
                {isDismissed ? "Restore" : "Not relevant"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TabFounderScoring() {
  return (
    <div className="card">
      <div className="h-eyebrow">How investors scored you</div>
      <div className="dim" style={{fontSize:13, marginTop:6, lineHeight:1.5}}>
        Percentile ranks benchmarked against Series A therapeutics evaluated by Venzyme in the last 12 months.
      </div>
      <div style={{marginTop:20}}>
        {MOCK.founderAxes.map((a,i)=>{
          const top = a.band.startsWith("Top");
          return (
            <div key={a.key} style={{padding:"16px 0", borderTop: i ? "1px solid var(--line)" : 0}}>
              <div className="row" style={{alignItems:"baseline", gap:12}}>
                <div style={{fontSize:15, fontWeight:500}}>{a.label}</div>
                <div className="spacer"/>
                <div className={"pill " + (top ? "pill--good" : "pill--warn")}>
                  <i className="dot"/>{a.band}
                </div>
                <div className="num" style={{fontSize:20, fontWeight:500, minWidth:40, textAlign:"right"}}>{a.score}</div>
              </div>
              <div style={{marginTop:8, position:"relative"}}>
                <div style={{height:6, background:"var(--line)", borderRadius:3, overflow:"hidden"}}>
                  <div style={{width:`${a.score}%`, height:"100%", background: top ? "var(--accent-hi)" : "var(--warn)"}}/>
                </div>
              </div>
              <div className="dim" style={{fontSize:13, marginTop:8, lineHeight:1.5}}>{a.note}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== Rails =====

function EvidenceRail({ activeSlide, setActiveSlide, analysis, analysisId }) {
  const slide = activeSlide;
  const hasReal = !!analysis;
  const citations = hasReal ? (analysis.citations || []) : Object.entries(MOCK.citations).map(([field, c]) => ({ field, slide: c.slide, quote: c.quote }));
  const current = slide ? citations.find(c => c.slide === slide) : null;
  const deckFilename = hasReal
    ? (analysis.documents?.[0]?.label || null)
    : "helix-therapeutics-seriesA-v4.pdf";
  const slideNumbers = citations.map(c => c.slide).filter(n => typeof n === "number");
  const maxSlide = slideNumbers.length ? Math.max(...slideNumbers) : (hasReal ? null : 28);

  async function openDeck() {
    if (!hasReal || !analysisId) { navigate("investor-hub"); return; }
    try {
      const resp = await apiGet(`/analyses/${analysisId}/deck-url/?disposition=inline`);
      window.open(resp.url, "_blank");
    } catch {
      navigate("investor-hub");
    }
  }

  return (
    <div className="card">
      <div className="row">
        <div className="h-eyebrow">Deck viewer</div>
        <div className="spacer"/>
        <div className="as-of">
          {slide ? `SLIDE ${slide}` : (maxSlide ? `${maxSlide} ${maxSlide === 1 ? "SLIDE" : "SLIDES"}` : "NO DECK")}
        </div>
      </div>
      <div className="slide-frame">
        {slide ? (
          <>
            <div className="slide-frame-num">§{slide}</div>
            {deckFilename && <div style={{fontSize:12, color:"var(--text-mute)", fontFamily:"var(--ff-mono)", marginTop:4}}>{deckFilename}</div>}
            {current && current.quote && (
              <div style={{marginTop:18, fontSize:13, color:"var(--text)", lineHeight:1.6, fontStyle:"italic"}}>
                "{current.quote}"
              </div>
            )}
          </>
        ) : (
          <div style={{textAlign:"center", color:"var(--text-dim)", fontSize:13, padding:"32px 12px", lineHeight:1.6}}>
            {citations.length > 0
              ? <>Click any <span className="cite-pill" style={{display:"inline-flex"}}>§N</span> citation to jump to the source slide.</>
              : hasReal
                ? "No slide-level citations extracted for this deck."
                : <>Click any <span className="cite-pill" style={{display:"inline-flex"}}>§N</span> citation to jump to the source slide.</>}
          </div>
        )}
      </div>
      <div className="row" style={{marginTop:12, gap:6}}>
        <button className="btn btn--quiet" disabled={!slide} onClick={() => setActiveSlide(Math.max(1, (slide || 1) - 1))}>← Prev</button>
        <button className="btn btn--quiet" disabled={!slide || (maxSlide && slide >= maxSlide)} onClick={() => setActiveSlide(Math.min(maxSlide || (slide + 1), (slide || 1) + 1))}>Next →</button>
        <div className="spacer"/>
        <button
          className="btn btn--quiet"
          onClick={openDeck}
          disabled={hasReal && !deckFilename}
        >Open full deck</button>
      </div>
    </div>
  );
}

function DecisionLog({ analysis }) {
  const hasReal = !!analysis;
  const entries = hasReal
    ? (analysis.decision_log || []).slice(0, 5).map(e => ({
        t: e.at ? _relTime(new Date(e.at)) : "—",
        who: e.actor || "system",
        action: e.note || _describeAction(e.action, e.meta),
        kind: e.action,
      }))
    : MOCK.decisionLog.slice(0, 3);

  return (
    <div className="card">
      <div className="row">
        <div className="h-eyebrow">Decision log</div>
        <div className="spacer"/>
        <span className="as-of">{hasReal ? "LIVE" : "DEMO"}</span>
      </div>
      <div style={{marginTop:12}}>
        {entries.length === 0 ? (
          <div className="dim" style={{fontSize:13, padding:"12px 0", fontStyle:"italic"}}>
            No decisions logged yet. Moving the pipeline stage, editing the thesis, or adding a note will appear here.
          </div>
        ) : entries.map((e, i) => (
          <div key={i} style={{
            display:"grid", gridTemplateColumns:"72px 1fr",
            gap:10, padding:"10px 0",
            borderTop: i ? "1px solid var(--line)" : 0
          }}>
            <div className="as-of">{e.t}</div>
            <div>
              <div style={{fontSize:13, lineHeight:1.4}}>{e.action}</div>
              <div className="dim" style={{fontSize:11, marginTop:2}}>{e.who}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function _relTime(d) {
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function ReportsCard({ analysis }) {
  const reports = analysis?.reports || [];
  if (!analysis || reports.length === 0) return null;
  return (
    <div className="card">
      <div className="h-eyebrow">Full reports</div>
      <div className="dim" style={{fontSize:12, marginTop:6, lineHeight:1.5}}>
        Detailed reports rendered from the extracted analysis.
      </div>
      <div style={{marginTop:12}}>
        {reports.map((r, i) => (
          <a
            key={r.key}
            href={r.url}
            style={{
              display:"block",
              padding:"10px 0",
              borderTop: i ? "1px solid var(--line)" : 0,
              color:"var(--text)",
              textDecoration:"none",
            }}
          >
            <div className="row" style={{gap:10, alignItems:"center"}}>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13, fontWeight:500}}>{r.label}</div>
                <div className="dim" style={{fontSize:11, marginTop:2, lineHeight:1.4}}>{r.description}</div>
              </div>
              <div className="dim" style={{fontSize:14}}>→</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function FounderNextSteps() {
  return (
    <div className="card">
      <div className="h-eyebrow">Your next steps</div>
      <ol style={{margin:"12px 0 0", paddingLeft:0, listStyle:"none"}}>
        {[
          "Draft answers for the 2 critical questions",
          "Update slide §22 (IP) with filing timeline",
          "Add a CMO slide before slide §23",
          "Request a re-score when ready",
        ].map((s,i)=>(
          <li key={i} style={{display:"grid", gridTemplateColumns:"24px 1fr", gap:10, padding:"10px 0", borderTop: i ? "1px solid var(--line)" : 0, alignItems:"baseline"}}>
            <div className="num" style={{fontSize:12, color:"var(--text-dim)"}}>0{i+1}</div>
            <div style={{fontSize:13, lineHeight:1.5}}>{s}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function DocumentsCard({ analysis, analysisId }) {
  const hasReal = !!analysis;
  const docs = hasReal ? (analysis.documents || []) : [
    { label: "helix-therapeutics-seriesA-v4.pdf", description: "Deck · 28 slides", s3_key: "demo" },
    { label: "helix-exec-summary.pdf", description: "Exec summary · 2 pages", s3_key: "demo" },
  ];

  async function openDoc(disposition = "inline") {
    if (!hasReal || !analysisId) { navigate("investor-hub"); return; }
    try {
      const resp = await apiGet(`/analyses/${analysisId}/deck-url/?disposition=${disposition}`);
      window.open(resp.url, "_blank");
    } catch {
      navigate("investor-hub");
    }
  }

  return (
    <div className="card">
      <div className="h-eyebrow">Documents</div>
      {docs.length === 0 ? (
        <div className="dim" style={{fontSize:13, marginTop:12, fontStyle:"italic"}}>
          No deck file attached to this analysis.
        </div>
      ) : (
        <div style={{marginTop:10}}>
          {docs.map((d, i) => (
            <div key={(d.s3_key || "") + i} className="row" style={{padding:"10px 0", borderTop: i === 0 ? "1px solid var(--line)" : "1px solid var(--line)"}}>
              <Ico n="doc" size={14}/>
              <div style={{marginLeft:4, flex:1, minWidth:0}}>
                <div style={{fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}} title={d.label}>{d.label}</div>
                <div className="dim" style={{fontSize:12}}>{d.description || "Deck"}</div>
              </div>
              <button
                className="btn btn--quiet"
                onClick={() => openDoc("inline")}
              >Open</button>
            </div>
          ))}
          {hasReal && (
            <div className="row" style={{marginTop:10, gap:6}}>
              <button className="btn btn--quiet" onClick={() => openDoc("attachment")}>Download</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

